import { pickT } from "@repo/shared";
import { Info } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ModalConfig } from "@/lib/types/flow";
import { FlowMarkdown } from "./flow-markdown";
import { T } from "./modal-renderer.i18n";

type ModalRendererProps = {
  config: ModalConfig;
  /** The words follow this (default en_GB), as `renderModal` passes it through. */
  language?: string;
};

export function ModalRenderer({ config, language }: ModalRendererProps) {
  const [open, setOpen] = useState(false);
  const t = pickT(T, language || "en_GB");

  if (!config.buttonLabel && !config.content) return null;

  return (
    <div>
      {/* The label is optional, and the two shapes are different buttons: with
          text it is a normal outline button, without it a square icon button
          that needs a tooltip to say anything at all. */}
      {config.buttonLabel ? (
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          <Info className="h-4 w-4" />
          {config.buttonLabel}
        </Button>
      ) : (
        <Button
          size="icon"
          tooltip={t.info}
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
        >
          <Info className="h-4 w-4" />
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] flex flex-col"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{config.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1">
            <FlowMarkdown content={config.content || ""} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
